import { AuthConfig } from 'src/AuthConfig';
import { Config } from '../../ConfigService/LoadConfig';
import { useCallback, useEffect, useState } from 'react';
import { logger } from 'src/Utils/LoggerService/LoggerService';
import axios from 'axios';
import { useClinicContext } from '../ClinicContext';
export interface IClinicLicenseInfo {
  entitlementId: string,
  licenseType: string,
  maximumSeats: number,
  availableSeats: number,
  licenseEndDate: string,
  activationDateInAura: string
}
export interface IClinicLicenseUserInfo {
  entitlementId: string,
  userEmail: string,
  loginTime: string,
  appDeviceInfo: string,
  sessionId: string
}
export interface IClinicStorageLicence {
  entitlementId:string,
  licenseEndDate:string,
  activationDateInAura:string,
  licenseType:string,
  maximumStorage:number,
  availableStorage: number
}

export interface IClinicUserAndStorageLicenseInfo{
  entitlementId: string,
  licenseType: string,
  licenseEndDate: string,
  maximumSeats: number,
  availableSeats: number,
  availableStorage: number,
  maximumStorage: number,
  activationDateInAura: string
}

export interface IClinicPaymentTransactions {
  id: string,
  clinicId: string,
  customerId: string,
  subscriptionId: string,
  sessionId: string,
  productId: string,
  paymentStatus: PaymentStatus,
  created: string,
  lastUpdated: string,
  userId: string,
  invoiceUrl: string,
  subscriptionType: SubscriptionType
}

/**
 * The clinic's renewal payment-failure state, as reported by the licensing service.
 *
 * Absent (undefined) when the clinic is up to date, so its presence alone is the signal that
 * something needs warning about.
 *
 * `daysUntilPause` comes from the backend rather than being derived here on purpose: the same
 * number appears in the reminder emails and drives the actual pause, and a client computing it
 * from a date would disagree across timezones and around midnight.
 */
export interface ISubscriptionPaymentFailure {
  paymentDueDate: string,
  pauseDate: string,
  daysUntilPause: number,
  isApproachingPause: boolean,
  isPaused: boolean,
  dataDeletionDate: string | null
}

export interface ILicensesInfo {
  clinicId: string,
  licenceInfo: IClinicLicenseInfo[],
  clinicStorageLicense: IClinicStorageLicence,
  clinicLicenceUserInfo: IClinicLicenseUserInfo[]
  clinicUserAndStorageLicenseInfo: IClinicUserAndStorageLicenseInfo,
  clinicPaymentTransactions: IClinicPaymentTransactions[],
  subscriptionPaymentFailure?: ISubscriptionPaymentFailure
}

export enum LicenseType {
  AuraAppPermanent = 'Aura App Permanent',
  AuraAppDemo = "Aura App Demo",
  AuraAppSubscription = 'Aura App Subscription',
  AuraAppTrial = 'Aura App Trial',
  AuraCloudOneyrSubscription = "Aura Cloud 1yr Subscription"
}

export enum PaymentStatus {
  None = 'None',
  Pending = 'Pending',
  Declined = 'Declined',
  Cancelled = 'Cancelled',
  Failed = 'Failed',
  Completed = 'Completed'
}

export enum SubscriptionType {
  New = 'New',
  Renewal = 'Renewal',
  Upgrade = 'Upgrade',
  Cancel = 'Cancel'
}

export interface ILicenseStats {
  total: number,
  inUse: number
}

export const useGetLicensesInfo = () => {
  const [licensesInfo, setLicensesInfo] = useState<ILicensesInfo>()
  const [licensesInfoLoading, setlicensesInfoLoading] = useState(false)
  const [licenseInfoError, setLicenseInfoError] = useState('')
  const {clinicInfo } = useClinicContext();
  
  const getLicencesInfo = useCallback(() => {
    const scope = Config.GetConfigData().auraLicensingService?.scopes[0];
    const baseUrl = Config.GetConfigData().auraLicensingService.serviceUrl;
    const requestUrl = `${baseUrl}api/v1/license/clinic/${clinicInfo?.clinicId}`;
    setlicensesInfoLoading(true);
    setLicenseInfoError('');
    AuthConfig.getToken(scope).then(
      (success) => {
        const headers = {
          Authorization: `Bearer ${success}`,
        };
        logger.logInformation(`Access token received successfully for scope :  ${scope} and request url: ${requestUrl}`);
        axios
          .get(requestUrl, { headers })
          .then(
            (response) => {
              setLicensesInfo(response.data);
              const status = response.status ? `Status Code: ${response.status}` : 'Status Code: N/A';
              logger.logInformation(`Get request to ${requestUrl} for license details was successful and ${status}.`);
              setLicenseInfoError('');
            },
            (error) => {
              const status = error.response && error.response.status ? `Status Code: ${error.response.status}` : 'Status Code: N/A';
              logger.logError(`Error occured to fetch license details for ${requestUrl}: ${status}.`)
              if (error.message && error.message !== '') {
                setLicenseInfoError(error.message);
              } else {
                setLicenseInfoError(error.response.data);
              }
              setLicensesInfo(undefined);
            }
          )
          .finally(() => {
            setlicensesInfoLoading(false);
          });
      },
      (error) => {
        setlicensesInfoLoading(false);
        setLicenseInfoError(error);
      }
    );
}, [clinicInfo?.clinicId]);

  useEffect(() => {
    if(clinicInfo){
      getLicencesInfo()
    }
  }, [clinicInfo, getLicencesInfo])


  return { licensesInfo, licenseInfoError, licensesInfoLoading, getLicencesInfo };
};
